import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Users2, ClipboardList, Wallet, FileSignature, Activity,
  BadgeCheck, Clock, UserCheck, ArrowRight, UserPlus, Pencil, Trash2, Stethoscope, Plus,
  Eye, Download, Send, FileText, List, LayoutGrid, RefreshCw,
} from "lucide-react";
import { Card, CardHead, Btn, Badge, Spinner, Avatar, EmptyState, statusTone, SearchInput } from "@/components/ui-kit";
import api, { docFileUrl } from "@/lib/api";
import ClinicianDetail from "@/pages/ops/ClinicianDetail";
import Modal from "@/pages/ops/Modal";
import RecordDetailModal from "@/components/RecordDetailModal";
import VisitReportModal, { REPORT_SECTIONS, reportPdfUrl } from "@/components/VisitReport";
import RosterViews from "@/components/RosterViews";
import ShiftEditModal from "@/components/ShiftEditModal";
import { ScoreBadge, StarRating } from "@/components/PerfScore";
import ClinicianList from "@/pages/ops/ClinicianList";
import ClinicianStatusControls, { ClinicianStatusBadges, ShiftWarning, isAvailable } from "@/pages/ops/ClinicianStatus";
import { CycleBadge, CycleHistory, ratio, reportSessionText } from "@/components/Cycle";
import ExpiryAlerts from "@/pages/ops/ExpiryAlerts";
import CertifiedBadge from "@/components/CertifiedBadge";

const compOf = (list, id) => list.find((c) => c.id === id)?.compliance;
const clinOpt = (c) => `${c.name}${c.compliance?.certified ? " ✓ Certified" : ""} · ${c.tier} · ${c.city}`;

const money = (n, c = "₹") => `${c}${Number(n || 0).toLocaleString("en-IN")}`;

export default function Ops() {
  const [tab, setTab] = useState("overview");
  const [ov, setOv] = useState(null);
  const [focus, setFocus] = useState(null);
  const loadOv = useCallback(() => api.get("/ops/overview").then((r) => setOv(r.data)), []);
  useEffect(() => { loadOv(); }, [loadOv]);
  const goTo = (t, f = null) => { setFocus(f); setTab(t); };

  const TABS = [
    { k: "overview", label: "Overview" },
    { k: "patients", label: "Patients" },
    { k: "assign", label: "Assignment Queue" },
    { k: "reports", label: "Visit Reports" },
    { k: "clinicians", label: "Clinicians" },
    { k: "billing", label: "Vendor Billing" },
    { k: "agreements", label: "Agreements" },
  ];

  return (
    <div data-testid="ops-portal">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-slate-900">PranIQ Operations</h1>
          <p className="text-[13px] text-slate-500 mt-0.5">Assign therapists, manage the clinician workforce, and run vendor billing.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`ops-tab-${t.k}`}
              className={`pq-chip ${tab === t.k ? "pq-chip-active" : ""}`}>{t.label}</button>
          ))}
        </div>
      </div>

      {tab === "overview" && <Overview ov={ov} goTo={goTo} />}
      {tab === "patients" && <OpsPatients onChange={loadOv} />}
      {tab === "assign" && <Assignment onChange={loadOv} initialStatus={focus || "awaiting"} />}
      {tab === "reports" && <OpsVisitReports />}
      {tab === "clinicians" && <Clinicians initialFilter={focus || "all"} />}
      {tab === "billing" && <VendorBilling onChange={loadOv} initialFilter={focus || "all"} />}
      {tab === "agreements" && <Agreements />}
    </div>
  );
}

function Overview({ ov, goTo }) {
  if (!ov) return <div className="flex justify-center py-20"><Spinner className="w-7 h-7 text-teal-600" /></div>;
  const KPI = [
    { label: "Clinicians", value: ov.clinicians_total, sub: `${ov.clinicians_verified} verified`, icon: Users2, tone: "bg-sky-50 text-sky-600", go: ["clinicians", "all"] },
    { label: "Awaiting Assignment", value: ov.orders_awaiting, sub: "treatment orders", icon: ClipboardList, tone: "bg-amber-50 text-amber-600", go: ["assign", "awaiting"] },
    { label: "Active Cases", value: ov.orders_active, sub: "in treatment", icon: Activity, tone: "bg-teal-50 text-teal-600", go: ["assign", "assigned"] },
    { label: "Open Vendor Invoices", value: money(ov.invoices_open, ov.currency), sub: `${ov.invoices_to_verify || 0} to verify`, icon: Wallet, tone: "bg-violet-50 text-violet-600", small: true, go: ["billing", "all"] },
  ];
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        {KPI.map((s) => (
          <Card key={s.label} hover onClick={() => goTo(s.go[0], s.go[1])} className="p-5 flex gap-3.5 items-start cursor-pointer" data-testid={`ops-kpi-${s.label.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${s.tone}`}><s.icon size={20} /></div>
            <div>
              <div className={`metric font-extrabold text-slate-900 leading-none ${s.small ? "text-xl" : "text-2xl"}`}>{s.value}</div>
              <div className="text-[12.5px] text-slate-500 mt-1 flex items-center gap-1">{s.label} <ArrowRight size={12} className="text-slate-300" /></div>
              <div className="text-[11px] text-slate-400 mt-0.5">{s.sub}</div>
            </div>
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-2">
          <CardHead title="Workforce readiness" />
          <div className="grid grid-cols-3 gap-3 mt-3">
            <Stat label="Verified" value={ov.clinicians_verified} icon={BadgeCheck} tone="text-emerald-600" onClick={() => goTo("clinicians", "verified")} testid="ops-stat-verified" />
            <Stat label="Pending verification" value={ov.clinicians_pending} icon={Clock} tone="text-amber-600" onClick={() => goTo("clinicians", "pending")} testid="ops-stat-pending" />
            <Stat label="Agreements pending" value={ov.agreements_pending} icon={FileSignature} tone="text-rose-600" onClick={() => goTo("agreements")} testid="ops-stat-agreements" />
          </div>
        </Card>
        <Card className="p-5">
          <CardHead title="Quick actions" />
          <div className="space-y-2 mt-2">
            <button onClick={() => goTo("assign", "awaiting")} data-testid="ops-quick-assign" className="w-full flex items-center gap-2 rounded-xl bg-slate-50 hover:bg-teal-50 px-4 py-3 text-[13px] font-semibold text-slate-700 transition-colors"><UserCheck size={16} className="text-teal-600" /> Assign therapists <ArrowRight size={14} className="ml-auto text-slate-400" /></button>
            <button onClick={() => goTo("clinicians", "all")} data-testid="ops-quick-clinicians" className="w-full flex items-center gap-2 rounded-xl bg-slate-50 hover:bg-teal-50 px-4 py-3 text-[13px] font-semibold text-slate-700 transition-colors"><Users2 size={16} className="text-teal-600" /> Manage clinicians <ArrowRight size={14} className="ml-auto text-slate-400" /></button>
            <button onClick={() => goTo("billing", "all")} data-testid="ops-quick-billing" className="w-full flex items-center gap-2 rounded-xl bg-slate-50 hover:bg-teal-50 px-4 py-3 text-[13px] font-semibold text-slate-700 transition-colors"><Wallet size={16} className="text-teal-600" /> Vendor billing <ArrowRight size={14} className="ml-auto text-slate-400" /></button>
          </div>
        </Card>
      </div>
      <ExpiryAlerts onOpen={() => goTo("clinicians", "all")} />
    </>
  );
}

function Stat({ label, value, icon: Icon, tone, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="rounded-xl bg-slate-50 hover:bg-slate-100 p-4 text-center transition-colors w-full">
      <Icon size={20} className={`mx-auto mb-1 ${tone}`} />
      <div className="metric text-2xl font-extrabold text-slate-900">{value}</div>
      <div className="text-[11.5px] text-slate-500 mt-0.5">{label}</div>
    </button>
  );
}

function orderDetailSections(view) {
  const orderedOn = view.created_at ? new Date(view.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : null;
  if (view.no_order) {
    return [{ heading: "Patient", fields: [
      { label: "Patient", value: view.patient_name, testid: "ops-order-view-patient" },
      { label: "Diagnosis", value: view.diagnosis_label },
      { label: "Owning doctor", value: view.doctor_name },
      { label: "Ordered on", value: orderedOn },
      { label: "Matching status", value: view.matching_status },
    ] }];
  }
  const d = view.diagnosis || {};
  const p = view.package || {};
  return [
    { heading: "Diagnosis — doctor's order", fields: [
      { label: "Primary diagnosis", value: d.primary_diagnosis || view.diagnosis_label, testid: "ops-order-view-diagnosis" },
      { label: "Body region", value: d.body_region },
      { label: "Affected side", value: d.affected_side },
      { label: "Severity", value: d.severity },
      { label: "Recovery stage", value: d.recovery_stage },
      { label: "Care mode", value: d.care_mode },
      { label: "Equipment", value: Array.isArray(d.equipment_items) ? d.equipment_items.join(", ") : d.equipment },
      { label: "Surgery date", value: d.surgery_date },
    ] },
    { heading: "Physiotherapy order", fields: [
      { label: "Plan", value: p.name || p.package_name, testid: "ops-order-view-plan" },
      { label: "Sessions", value: p.sessions != null ? `${p.sessions}` : null },
      { label: "Duration", value: p.weeks != null ? `${p.weeks} weeks` : d.duration },
      { label: "Frequency", value: p.frequency || d.frequency },
      { label: "Milestones", value: (p.milestones || []).join(" → ") || null, full: true },
    ] },
    { heading: "Order", fields: [
      { label: "Patient", value: view.patient_name, testid: "ops-order-view-patient-name" },
      { label: "Owning doctor", value: view.doctor_name },
      { label: "Assigned clinician", value: view.assigned_physio?.name, testid: "ops-order-view-clinician" },
      { label: "Ordered on", value: orderedOn },
      { label: "Order value", value: money(p.amount, p.currency) },
      { label: "Matching status", value: view.matching_status },
    ] },
  ];
}

function Assignment({ onChange, initialStatus = "awaiting" }) {
  const [status, setStatus] = useState(initialStatus);
  const [orders, setOrders] = useState(null);
  const [clinicians, setClinicians] = useState([]);
  const [view, setView] = useState(null);
  const [addOrder, setAddOrder] = useState(null);
  const load = useCallback(() => api.get(`/ops/orders?status=${status}`).then((r) => setOrders(r.data)), [status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/ops/clinicians").then((r) => setClinicians(r.data.filter((c) => c.credential_status === "Verified" && isAvailable(c)))); }, []);

  const assign = async (o, clinician_id) => {
    if (!clinician_id) return;
    try {
      if (o.no_order) await api.post(`/ops/patients/${o.patient_id}/assign`, { clinician_id });
      else await api.post(`/ops/orders/${o.id}/assign`, { clinician_id });
      toast.success("Therapist assigned"); load(); onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Assignment failed"); }
  };

  return (
    <Card>
      <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100">
        <button onClick={() => setStatus("awaiting")} data-testid="ops-assign-filter-awaiting" className={`pq-chip ${status === "awaiting" ? "pq-chip-active" : ""}`}>Awaiting assignment</button>
        <button onClick={() => setStatus("assigned")} data-testid="ops-assign-filter-assigned" className={`pq-chip ${status === "assigned" ? "pq-chip-active" : ""}`}>Assigned</button>
      </div>
      {!orders ? <div className="flex justify-center py-16"><Spinner className="w-6 h-6 text-teal-600" /></div>
        : orders.length === 0 ? <EmptyState icon={<ClipboardList size={24} />} title="Nothing here" subtitle={status === "awaiting" ? "Every patient has a clinician assigned." : "No assigned orders yet."} />
        : (
        <div className="overflow-x-auto"><table className="w-full">
          <thead><tr>{["Order", "Patient", "Diagnosis", status === "assigned" ? "Therapist" : "Assign therapist", "Physiotherapy order"].map((h) => <th key={h} className="pq-label px-5 py-3 text-left border-b border-slate-100">{h}</th>)}</tr></thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} onClick={() => setView(o)} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer" data-testid={`ops-order-row-${o.id}`}>
                <td className="px-5 py-3 text-[13px] font-semibold">{o.no_order ? <Badge tone="amber">No order yet</Badge> : <div className="space-y-1"><div>{o.ref_no}</div><CycleBadge cycle={o.cycle} testid={`ops-order-cycle-${o.id}`} /></div>}</td>
                <td className="px-5 py-3"><div className="flex items-center gap-2"><Avatar name={o.patient_name} size={30} /><span className="text-[13px] font-semibold">{o.patient_name}</span></div></td>
                <td className="px-5 py-3 text-[13px]">{o.diagnosis_label}</td>
                <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                  {status === "assigned" ? (
                    <span className="inline-flex items-center gap-2 text-[13px] font-semibold">{o.assigned_physio ? <Avatar name={o.assigned_physio.name} size={24} src={docFileUrl(o.assigned_physio.photo_doc_id)} /> : null}{o.assigned_physio?.name || "—"}<CertifiedBadge comp={compOf(clinicians, o.assigned_physio?.id)} testid={`ops-order-certified-${o.id}`} /></span>
                  ) : (
                    <div className="flex items-center gap-2">
                      <select className="pq-input py-1.5 max-w-[220px]" defaultValue="" onChange={(e) => assign(o, e.target.value)} data-testid={`ops-assign-select-${o.id}`}>
                        <option value="" disabled>Select clinician…</option>
                        {clinicians.map((c) => <option key={c.id} value={c.id}>{clinOpt(c)}</option>)}
                      </select>
                      {o.no_order && <Btn size="sm" onClick={() => setAddOrder(o)} data-testid={`ops-add-order-${o.patient_id}`}><Plus size={13} /> Add order</Btn>}
                    </div>
                  )}
                </td>
                <td className="px-5 py-3 text-[12.5px] text-slate-700">{o.no_order ? "—" : <span><span className="font-semibold">{o.package?.sessions ?? "—"}</span> sessions · {o.package?.weeks ?? "—"} wks{o.package?.frequency || o.diagnosis?.frequency ? ` · ${o.package?.frequency || o.diagnosis?.frequency}` : ""}</span>}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
      {view && <RecordDetailModal
        title={view.no_order ? view.patient_name : view.ref_no}
        subtitle={view.no_order ? "No treatment order yet" : `Doctor's treatment order · ${view.patient_name}`}
        badge={<Badge tone={statusTone(view.status)}>{view.status}</Badge>}
        onClose={() => setView(null)}
        sections={orderDetailSections(view)} />}
      {addOrder && <AddOrderModal row={addOrder} onClose={() => setAddOrder(null)} onDone={() => { setAddOrder(null); load(); onChange && onChange(); }} />}
    </Card>
  );
}

function AddOrderModal({ row, onClose, onDone }) {
  const [f, setF] = useState({ diagnosis_label: row.diagnosis_label !== "—" ? row.diagnosis_label : "", amount: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/ops/patients/${row.patient_id}/order`, { diagnosis_label: f.diagnosis_label, amount: Number(f.amount) || 0 });
      toast.success("Treatment order created"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create order"); setSaving(false); }
  };
  return (
    <Modal title={`Add treatment order — ${row.patient_name}`} onClose={onClose}>
      <div className="space-y-3">
        {!row.has_doctor && <p className="text-[12px] text-amber-600" data-testid="ops-add-order-no-doctor">This patient has no owning doctor. Set the doctor on the patient record first.</p>}
        <div><label className="pq-label">Diagnosis</label><input className="pq-input" value={f.diagnosis_label} onChange={(e) => setF({ ...f, diagnosis_label: e.target.value })} placeholder="e.g. Post-op knee rehabilitation" data-testid="ops-add-order-diagnosis" /></div>
        <div><label className="pq-label">Order amount (optional)</label><input type="number" className="pq-input" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="Leave blank — Finance can price it later" data-testid="ops-add-order-amount" /></div>
        <div className="flex justify-end gap-2 pt-1">
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" onClick={save} disabled={saving || !row.has_doctor} data-testid="ops-add-order-save">{saving ? <Spinner className="w-4 h-4" /> : "Create order"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

function Clinicians({ initialFilter = "all" }) {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [vf, setVf] = useState(initialFilter);
  const [sf, setSf] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [openTab, setOpenTab] = useState("performance");
  const [view, setView] = useState("list");
  const [mode, setMode] = useState("directory");
  const load = useCallback(() => api.get("/ops/clinicians").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!list) return <div className="flex justify-center py-20"><Spinner className="w-7 h-7 text-teal-600" /></div>;
  const modeSwitch = (
    <div className="flex gap-2 mb-4" data-testid="ops-clinicians-mode">
      {[["directory", "Directory"], ["roster", "Roster board"]].map(([k, l]) => (
        <button key={k} onClick={() => setMode(k)} data-testid={`ops-clinicians-mode-${k}`} className={`pq-chip ${mode === k ? "pq-chip-active" : ""}`}>{l}</button>
      ))}
    </div>
  );
  if (mode === "roster") return <>{modeSwitch}<OpsRosterBoard /></>;
  const shown = list.filter((c) => {
    if (vf === "verified" && c.credential_status !== "Verified") return false;
    if (vf === "pending" && c.credential_status === "Verified") return false;
    if (sf !== "all" && acctStatus(c) !== sf) return false;
    return !q || c.name.toLowerCase().includes(q.toLowerCase()) || (c.city || "").toLowerCase().includes(q.toLowerCase());
  });
  const FILTERS = [{ k: "all", label: "All" }, { k: "verified", label: "Verified" }, { k: "pending", label: "Pending verification" }];

  return (
    <>
      {modeSwitch}
      <div className="flex justify-between items-center gap-3 mb-3 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          {FILTERS.map((f) => (
            <button key={f.k} onClick={() => setVf(f.k)} data-testid={`ops-clinician-filter-${f.k}`} className={`pq-chip ${vf === f.k ? "pq-chip-active" : ""}`}>{f.label}</button>
          ))}
          <span className="w-px bg-slate-200 mx-1 hidden sm:block" />
          {STATUS_FILTERS.map(([k, l]) => (
            <button key={k} onClick={() => setSf(k)} data-testid={`ops-clinician-status-filter-${k}`} className={`pq-chip ${sf === k ? "pq-chip-active" : ""}`}>
              {l}{k !== "all" && <span className="ml-1 opacity-70" data-testid={`ops-clinician-status-count-${k}`}>{list.filter((c) => acctStatus(c) === k).length}</span>}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shrink-0" data-testid="ops-clinician-view">
            {[["list", "List", List], ["grid", "Cards", LayoutGrid]].map(([k, l, I]) => (
              <button key={k} onClick={() => setView(k)} data-testid={`ops-clinician-view-${k}`}
                className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[12px] font-semibold transition-colors ${view === k ? "bg-[#1A3A6D] text-white" : "text-slate-600 hover:bg-slate-100"}`}><I size={13} />{l}</button>
            ))}
          </div>
          <SearchInput testid="ops-clinician-search" value={q} onChange={setQ} placeholder="Search clinicians…" className="sm:w-72" />
        </div>
      </div>
      {shown.length === 0 ? <EmptyState icon={<Users2 size={24} />} title="No clinicians" subtitle="No clinicians match this filter." /> : view === "list" ? (
        <ClinicianList list={shown} onChange={load} onOpen={(id, t = "performance") => { setOpenTab(t); setOpenId(id); }} />
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {shown.map((c) => (
          <Card key={c.id} hover className={`p-5 cursor-pointer ${isAvailable(c) ? "" : "opacity-80 bg-slate-50"}`} onClick={() => { setOpenTab("performance"); setOpenId(c.id); }} data-testid={`ops-clinician-card-${c.id}`}>
            <div className="flex items-start gap-3">
              <Avatar name={c.name} size={44} src={docFileUrl(c.photo_doc_id)} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[14px] truncate">{c.name}</div>
                <div className="text-[12px] text-slate-500 truncate">{c.specialty}</div>
                <div className="text-[11px] text-slate-400">{c.city} · {c.prq_id || "—"}</div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Badge tone={c.credential_status === "Verified" ? "emerald" : "amber"}>{c.credential_status || "Pending"}</Badge>
                <ClinicianStatusBadges c={c} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-4 text-center">
              <MiniStat label="Active" value={c.perf.active_cases} />
              <MiniStat label="Sessions" value={`${c.perf.adherence}%`} />
              <div className="rounded-lg bg-slate-50 py-2" data-testid={`ops-card-score-${c.id}`}>
                <div className="text-[15px]"><ScoreBadge score={c.perf.perf_score} isNew={c.perf.is_new} /></div>
                <div className="text-[10.5px] text-slate-500">Score</div>
              </div>
            </div>
            <div className="mt-2"><StarRating value={c.perf.patient_rating} count={c.perf.patient_rating_count} testid={`ops-card-stars-${c.id}`} /></div>
            <div className="flex items-center gap-2 mt-3 text-[11.5px]">
              <Badge tone={c.agreement_active ? "emerald" : "slate"}>{c.agreement_active ? "Agreement active" : "No agreement"}</Badge>
              <Badge tone={c.perf.payout_pending > 0 ? "amber" : "slate"}>{money(c.perf.payout_pending)} pending</Badge>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
              <ClinicianStatusControls c={c} onChange={load} />
              <ShiftWarning c={c} onChange={load} />
            </div>
          </Card>
        ))}
      </div>
      )}
      {openId && <ClinicianDetail key={`${openId}-${openTab}`} cid={openId} initialTab={openTab} onClose={() => setOpenId(null)} onChange={load} />}
    </>
  );
}

const STATUS_FILTERS = [["all", "Any status"], ["active", "Active"], ["deactivated", "Deactivated"], ["suspended", "Suspended"]];
const acctStatus = (c) => (c.account_suspended ? "suspended" : c.active === false ? "deactivated" : "active");

function MiniStat({ label, value }) {
  return <div className="rounded-lg bg-slate-50 py-2"><div className="metric text-[15px] font-bold text-slate-900">{value}</div><div className="text-[10.5px] text-slate-500">{label}</div></div>;
}

const GENDERS = ["Male", "Female", "Other"];
const PSTATUS = ["New", "On Track", "Review Needed", "At Risk", "Completed", "Discharged"];
const EMPTY_PATIENT = { doctor_id: "", name: "", age: "", gender: "Male", dob: "", phone: "", email: "", facility: "", diagnosis: "", conditions: "", allergies: "", status: "New", planned_sessions: 0 };

function OpsPatients({ onChange }) {
  const [list, setList] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [clinicians, setClinicians] = useState([]);
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null); // { mode: "create"|"edit", patient }
  const [view, setView] = useState(null);
  const [crm, setCrm] = useState(false);
  const [cycleFor, setCycleFor] = useState(null);

  const load = useCallback(() => api.get("/ops/patients", { params: q ? { q } : {} }).then((r) => setList(r.data)), [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    api.get("/ops/doctors").then((r) => setDoctors(r.data));
    api.get("/ops/clinicians").then((r) => setClinicians(r.data.filter((c) => c.credential_status === "Verified" && isAvailable(c))));
  }, []);

  const assign = async (rid, clinician_id) => {
    if (!clinician_id) return;
    try { await api.post(`/ops/orders/${rid}/assign`, { clinician_id }); toast.success("Clinician assigned"); load(); onChange && onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Assignment failed"); }
  };
  const assignNew = async (pid, clinician_id) => {
    if (!clinician_id) return;
    try { await api.post(`/ops/patients/${pid}/assign`, { clinician_id }); toast.success("Clinician assigned"); load(); onChange && onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Assignment failed"); }
  };
  const del = async (p) => {
    if (!window.confirm(`Permanently delete ${p.name} (${p.pranit_id})? This cannot be undone.`)) return;
    try { await api.delete(`/ops/patients/${p.id}`); toast.success("Patient deleted"); load(); onChange && onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid="ops-patients">
      <div className="flex justify-between items-center gap-3 mb-3 flex-wrap">
        <SearchInput testid="ops-patient-search" value={q} onChange={setQ} placeholder="Search patients…" className="sm:w-72" />
        <div className="flex gap-2">
          <Btn onClick={() => setCrm(true)} data-testid="ops-crm-link-btn"><Stethoscope size={15} /> Link CorreCore CRM</Btn>
          <Btn variant="primary" onClick={() => setModal({ mode: "create", patient: EMPTY_PATIENT })} data-testid="ops-add-patient-btn"><UserPlus size={15} /> Add patient</Btn>
        </div>
      </div>
      <Card>
        {!list ? <div className="flex justify-center py-16"><Spinner className="w-6 h-6 text-teal-600" /></div>
          : list.length === 0 ? <EmptyState icon={<Users2 size={24} />} title="No patients" subtitle="Add a patient on a doctor's behalf, or import them from CorreCore CRM." />
          : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead><tr>{["Patient", "ID", "Doctor", "Diagnosis", "Clinician", "Sessions", "Status", ""].map((h) => <th key={h} className="pq-label px-4 py-3 text-left border-b border-slate-100">{h}</th>)}</tr></thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id} onClick={() => setView(p)} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer" data-testid={`ops-patient-row-${p.id}`}>
                  <td className="px-4 py-3"><div className="flex items-center gap-2.5"><Avatar name={p.name} size={32} /><div><div className="font-semibold text-[13px]">{p.name}{p.crm_source ? <Badge tone="sky" className="ml-1.5">CRM</Badge> : null}</div><div className="text-[11.5px] text-slate-400">{p.age ?? "—"} · {p.gender}{p.phone ? ` · ${p.phone}` : ""}</div></div></div></td>
                  <td className="px-4 py-3 metric text-[12px] text-slate-500">{p.pranit_id}</td>
                  <td className="px-4 py-3 text-[13px] text-slate-600"><div className="flex items-center gap-2">{p.doctor_name ? <Avatar name={p.doctor_name} size={24} src={docFileUrl(p.doctor_photo)} /> : null}<span>{p.doctor_name || "—"}</span></div></td>
                  <td className="px-4 py-3 text-[13px]">{p.diagnosis || "—"}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    {p.assigned_clinician ? <span className="inline-flex items-center gap-1 text-[13px] font-semibold">{p.assigned_clinician}<CertifiedBadge comp={compOf(clinicians, p.assigned_clinician_id)} testid={`ops-patient-certified-${p.id}`} /></span>
                      : (p.awaiting_order || p.owner_id) ? (
                        <select className="pq-input py-1.5 max-w-[200px]" defaultValue="" onChange={(e) => p.awaiting_order ? assign(p.awaiting_order.id, e.target.value) : assignNew(p.id, e.target.value)} data-testid={`ops-patient-assign-${p.id}`}>
                          <option value="" disabled>Assign clinician…</option>
                          {clinicians.map((c) => <option key={c.id} value={c.id}>{clinOpt(c)}</option>)}
                        </select>
                      ) : <span className="text-[12px] text-slate-400">No order yet</span>}
                  </td>
                  <td className="px-4 py-3 text-[12.5px] text-slate-600" data-testid={`ops-patient-sessions-${p.id}`}>
                    {p.latest_order ? <div className="space-y-1 max-w-[260px]"><div className="whitespace-nowrap font-semibold text-slate-800">{ratio(p.latest_order)}</div><CycleHistory h={p} testid={`ops-patient-cycles-${p.id}`} /></div>
                      : <span>{p.planned_sessions || 0} planned</span>}
                  </td>
                  <td className="px-4 py-3"><Badge tone={statusTone(p.status)}>{p.status}</Badge></td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}><div className="flex gap-1.5 justify-end">
                    <Btn size="sm" onClick={() => setModal({ mode: "edit", patient: p })} data-testid={`ops-patient-edit-${p.id}`}><Pencil size={13} /> Edit</Btn>
                    <Btn size="sm" className="pq-btn-danger" onClick={() => del(p)} data-testid={`ops-patient-delete-${p.id}`}><Trash2 size={13} /></Btn>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </Card>
      {modal && <PatientForm modal={modal} doctors={doctors} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); onChange && onChange(); }} />}
      {view && <RecordDetailModal
        title={view.name}
        subtitle={`${view.pranit_id || "—"} · ${view.age ?? "—"} · ${view.gender || "—"}`}
        badge={<Badge tone={statusTone(view.status)}>{view.status}</Badge>}
        onClose={() => setView(null)}
        sections={[
          { heading: "Contact", fields: [
            { label: "Phone", value: view.phone, testid: "ops-view-phone" },
            { label: "Email", value: view.email },
            { label: "Date of birth", value: view.dob },
            { label: "Facility", value: view.facility },
          ] },
          { heading: "Care", fields: [
            { label: "Owning doctor", value: view.doctor_name, testid: "ops-view-doctor" },
            { label: "Assigned clinician", value: view.assigned_clinician, testid: "ops-view-clinician" },
            { label: "Diagnosis", value: view.diagnosis, full: true },
            { label: "Conditions", value: view.conditions, full: true },
            { label: "Allergies", value: view.allergies, full: true },
          ] },
          (view.crm_source ? { heading: "CRM", fields: [
            { label: "Source", value: view.crm_source },
            { label: "CRM ID", value: view.crm_id },
          ] } : null),
        ].filter(Boolean)}>
        {view.latest_order && (
          <div className="rounded-xl border border-sky-100 bg-sky-50/60 px-4 py-3 space-y-3" data-testid="ops-view-cycles">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="pq-label mb-0.5">Therapy cycles</div>
                <div className="text-[13.5px] text-slate-800"><b data-testid="ops-view-cycle-count">Cycle count: {view.cycles?.length || 1}</b> · Sessions <b data-testid="ops-view-sessions-done">{ratio(view.latest_order)}</b></div>
              </div>
              <Btn size="sm" variant="primary" onClick={() => { setCycleFor(view); setView(null); }} data-testid="ops-view-next-cycle"><RefreshCw size={13} /> Next cycle</Btn>
            </div>
            <SessionEditLog log={view.sessions_edit_log} />
          </div>
        )}
      </RecordDetailModal>}
      {crm && <CrmImport doctors={doctors} onClose={() => setCrm(false)} onDone={() => { setCrm(false); load(); onChange && onChange(); }} />}
      {cycleFor && <NextCycleModal p={cycleFor} onClose={() => setCycleFor(null)} onDone={() => { setCycleFor(null); load(); onChange && onChange(); }} />}
    </div>
  );
}

function SessionEditLog({ log = [] }) {
  return (
    <div className="pt-3 border-t border-dashed border-sky-200" data-testid="ops-view-sessions-log">
      <div className="pq-label mb-1.5">Session-count changes</div>
      {log.length === 0 ? <p className="text-[12px] text-slate-500" data-testid="ops-view-sessions-log-empty">No manual changes — counts come from clinician visit logs.</p> : (
        <ul className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
          {log.map((e, i) => (
            <li key={i} className="flex flex-wrap items-baseline gap-x-2 text-[12.5px] text-slate-700" data-testid={`ops-view-sessions-log-${i}`}>
              <b className="text-slate-900">{e.from} → {e.to}</b>
              <span>by {e.by || "—"}{e.role ? ` (${e.role})` : ""}</span>
              <span className="text-slate-400">{new Date(e.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })} · Cycle {e.cycle || 1}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NextCycleModal({ p, onClose, onDone }) {
  const [n, setN] = useState(0);
  const [busy, setBusy] = useState(false);
  const next = (p.latest_order?.cycle || 1) + 1;
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/ops/patients/${p.id}/next-cycle`, { sessions: Number(n) || 0 });
      toast.success(`Cycle ${r.data.cycle} started — ${r.data.ref_no}`);
      if (r.data.assign_note) toast.warning(r.data.assign_note);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start the next cycle"); }
    setBusy(false);
  };
  return (
    <Modal title={`Start cycle ${next} — ${p.name}`} subtitle="Continuing order: sessions restart from 0, same doctor and clinician. The current order is closed." onClose={onClose}>
      <label className="block"><span className="pq-label block mb-1">Number of sessions (0 = not set yet)</span>
        <input type="number" min={0} className="pq-input" value={n} onChange={(e) => setN(e.target.value)} data-testid="ops-next-cycle-sessions" /></label>
      <div className="flex justify-end gap-2 mt-4"><Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" onClick={go} disabled={busy} data-testid="ops-next-cycle-confirm">{busy ? <Spinner className="w-4 h-4" /> : `Start cycle ${next}`}</Btn></div>
    </Modal>
  );
}

function PatientForm({ modal, doctors, onClose, onDone }) {
  const edit = modal.mode === "edit";
  const p = modal.patient;
  const [f, setF] = useState({
    doctor_id: p.owner_id || "", name: p.name || "", age: p.age ?? "", gender: p.gender || "Male",
    dob: p.dob || "", phone: p.phone || "", email: p.email || "", facility: p.facility || "",
    diagnosis: p.diagnosis || "", conditions: p.conditions || "", allergies: p.allergies || "", status: p.status || "New",
    sessions: p.latest_order ? p.latest_order.sessions_total || 0 : p.planned_sessions || 0,
    done: p.latest_order?.sessions_completed || 0,
  });
  const hasOrder = edit && !!p.latest_order;
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.doctor_id) return toast.error("Select the doctor this patient belongs to");
    if (!f.name.trim()) return toast.error("Patient name is required");
    setSaving(true);
    try {
      const { done, ...rest } = f;
      const body = { ...rest, sessions: Number(f.sessions) || 0, ...(hasOrder ? { sessions_completed: Number(done) || 0 } : {}) };
      if (edit) await api.put(`/ops/patients/${p.id}`, body);
      else await api.post("/ops/patients", body);
      toast.success(edit ? "Patient updated" : "Patient added on doctor's behalf");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSaving(false);
  };
  return (
    <Modal title={edit ? `Edit patient — ${p.pranit_id}` : "Add patient (on doctor's behalf)"} subtitle="Ops can create and manage patient records for any doctor" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <label className="block col-span-2"><span className="pq-label block mb-1">Doctor (owner)</span>
          <select className="pq-input" value={f.doctor_id} onChange={set("doctor_id")} data-testid="ops-patient-doctor">
            <option value="" disabled>Select doctor…</option>
            {doctors.map((d) => <option key={d.id} value={d.id}>{d.name}{d.specialty ? ` · ${d.specialty}` : ""}{d.email ? ` · ${d.email}` : ""}</option>)}
          </select></label>
        <label className="block col-span-2"><span className="pq-label block mb-1">Full name</span><input className="pq-input" value={f.name} onChange={set("name")} data-testid="ops-patient-name" /></label>
        <label className="block"><span className="pq-label block mb-1">Age</span><input type="number" className="pq-input" value={f.age} onChange={set("age")} data-testid="ops-patient-age" /></label>
        <label className="block"><span className="pq-label block mb-1">Gender</span><select className="pq-input" value={f.gender} onChange={set("gender")} data-testid="ops-patient-gender">{GENDERS.map((g) => <option key={g}>{g}</option>)}</select></label>
        <label className="block"><span className="pq-label block mb-1">Phone</span><input className="pq-input" value={f.phone} onChange={set("phone")} data-testid="ops-patient-phone" /></label>
        <label className="block"><span className="pq-label block mb-1">DOB</span><input className="pq-input" value={f.dob} onChange={set("dob")} placeholder="YYYY-MM-DD" data-testid="ops-patient-dob" /></label>
        <label className="block col-span-2"><span className="pq-label block mb-1">Email</span><input type="email" className="pq-input" value={f.email} onChange={set("email")} data-testid="ops-patient-email" /></label>
        <label className="block col-span-2"><span className="pq-label block mb-1">Facility</span><input className="pq-input" value={f.facility} onChange={set("facility")} data-testid="ops-patient-facility" /></label>
        <label className="block col-span-2"><span className="pq-label block mb-1">Diagnosis</span><input className="pq-input" value={f.diagnosis} onChange={set("diagnosis")} data-testid="ops-patient-diagnosis" /></label>
        <label className="block col-span-2"><span className="pq-label block mb-1">Conditions</span><input className="pq-input" value={f.conditions} onChange={set("conditions")} data-testid="ops-patient-conditions" /></label>
        <label className="block"><span className="pq-label block mb-1">Allergies</span><input className="pq-input" value={f.allergies} onChange={set("allergies")} data-testid="ops-patient-allergies" /></label>
        <label className="block"><span className="pq-label block mb-1">Status</span><select className="pq-input" value={f.status} onChange={set("status")} data-testid="ops-patient-status">{PSTATUS.map((s) => <option key={s}>{s}</option>)}</select></label>
        {hasOrder && (
          <label className="block"><span className="pq-label block mb-1">Sessions completed</span>
            <input type="number" min={0} className="pq-input" value={f.done} onChange={set("done")} data-testid="ops-patient-sessions-done" /></label>
        )}
        <label className={`block ${hasOrder ? "" : "col-span-2"}`}><span className="pq-label block mb-1">Total sessions{edit && p.latest_order?.cycle > 1 ? ` (cycle ${p.latest_order.cycle})` : ""} — 0 = not set</span>
          <input type="number" min={0} className="pq-input" value={f.sessions} onChange={set("sessions")} data-testid="ops-patient-sessions" /></label>
        {hasOrder && <p className="col-span-2 text-[12px] text-slate-500" data-testid="ops-patient-sessions-preview">Shows as <b className="text-slate-800">{ratio({ sessions_completed: Number(f.done) || 0, sessions_total: Number(f.sessions) || 0 })}</b> · completed is normally updated by the clinician; edits here are recorded with your name.</p>}
      </div>
      <div className="flex justify-end gap-2 mt-4"><Btn onClick={onClose}>Cancel</Btn><Btn variant="primary" onClick={save} disabled={saving} data-testid="ops-patient-save">{saving ? <Spinner className="w-4 h-4" /> : edit ? "Save changes" : "Add patient"}</Btn></div>
    </Modal>
  );
}

function CrmImport({ doctors, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [doctorId, setDoctorId] = useState("");
  const [picked, setPicked] = useState({});
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { api.get("/ops/crm/correcore/patients").then((r) => setData(r.data)).catch((e) => { const msg = e.response?.data?.detail || "CRM fetch failed"; setError(msg); toast.error(msg); }); }, []);
  const toggle = (id) => setPicked((p) => ({ ...p, [id]: !p[id] }));
  const doImport = async () => {
    if (!doctorId) return toast.error("Select the doctor to import under");
    const chosen = (data?.patients || []).filter((p) => picked[p.crm_id || p.id]);
    if (chosen.length === 0) return toast.error("Select at least one patient");
    setImporting(true);
    try {
      const { data: res } = await api.post("/ops/crm/correcore/import", { doctor_id: doctorId, patients: chosen });
      toast.success(`Imported ${res.created} patient(s)${res.skipped ? `, ${res.skipped} already linked` : ""}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    setImporting(false);
  };
  return (
    <Modal title="Link CorreCore CRM" subtitle="Fetch patients in treatment from CorreCore and import them into PranIQ" onClose={onClose} wide>
      {error ? <div className="py-10 text-center" data-testid="crm-error-state"><div className="text-[13px] text-rose-600 font-semibold mb-1">Couldn't fetch from CorreCore CRM</div><div className="text-[12px] text-slate-500 max-w-md mx-auto">{error}</div><Btn className="mt-4" onClick={onClose} data-testid="crm-error-close">Close</Btn></div>
      : !data ? <div className="flex justify-center py-12"><Spinner className="w-6 h-6 text-teal-600" /></div> : (
        <>
          {data.source === "mock" && <div className="mb-3 text-[12px] bg-amber-50 text-amber-700 rounded-lg px-3 py-2" data-testid="crm-mock-banner">Showing SAMPLE CorreCore data — set CORRECORE_API_URL &amp; CORRECORE_API_KEY to pull live records.</div>}
          <label className="block mb-3"><span className="pq-label block mb-1">Import under doctor</span>
            <select className="pq-input max-w-sm" value={doctorId} onChange={(e) => setDoctorId(e.target.value)} data-testid="crm-doctor-select">
              <option value="" disabled>Select doctor…</option>
              {doctors.map((d) => <option key={d.id} value={d.id}>{d.name}{d.specialty ? ` · ${d.specialty}` : ""}{d.email ? ` · ${d.email}` : ""}</option>)}
            </select></label>
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto"><table className="w-full">
              <thead><tr>{["", "Patient", "Age/Gender", "Phone", "Diagnosis", "CRM status"].map((h) => <th key={h} className="pq-label px-3 py-2 text-left border-b border-slate-100">{h}</th>)}</tr></thead>
              <tbody>
                {(data.patients || []).map((p) => { const id = p.crm_id || p.id; return (
                  <tr key={id} className="border-b border-slate-100 last:border-0" data-testid={`crm-row-${id}`}>
                    <td className="px-3 py-2"><input type="checkbox" className="accent-teal-600 w-4 h-4" checked={!!picked[id]} onChange={() => toggle(id)} data-testid={`crm-pick-${id}`} /></td>
                    <td className="px-3 py-2 text-[13px] font-semibold">{p.name}</td>
                    <td className="px-3 py-2 text-[13px]">{p.age ?? "—"} · {p.gender || "—"}</td>
                    <td className="px-3 py-2 text-[13px]">{p.phone || "—"}</td>
                    <td className="px-3 py-2 text-[13px]">{p.diagnosis || "—"}</td>
                    <td className="px-3 py-2"><Badge tone="sky">{p.status || "—"}</Badge></td>
                  </tr>
                ); })}
              </tbody>
            </table></div>
          </div>
          <div className="flex justify-end gap-2 mt-4"><Btn onClick={onClose}>Cancel</Btn><Btn variant="primary" onClick={doImport} disabled={importing} data-testid="crm-import-submit">{importing ? <Spinner className="w-4 h-4" /> : "Import selected"}</Btn></div>
        </>
      )}
    </Modal>
  );
}

function VendorBilling({ onChange, initialFilter = "all" }) {
  const [invoices, setInvoices] = useState(null);
  const [clinicians, setClinicians] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [rejecting, setRejecting] = useState(null);
  const [sf, setSf] = useState("submitted");
  const load = useCallback(() => api.get("/ops/invoices").then((r) => setInvoices(r.data)), []);
  useEffect(() => { load(); api.get("/ops/clinicians").then((r) => setClinicians(r.data)); }, [load]);

  const setStatus = async (iid, status, extra = {}) => {
    await api.put(`/ops/invoices/${iid}`, { status, ...extra });
    toast.success(`Invoice ${status.toLowerCase()}`); load(); onChange && onChange();
  };
  const shown = invoices ? invoices.filter((i) => sf === "all" || i.status === (sf === "submitted" ? "Submitted" : sf === "approved" ? "Approved" : "Paid")) : [];
  const SF = [{ k: "submitted", label: "Pending verification" }, { k: "approved", label: "Approved" }, { k: "paid", label: "Paid" }, { k: "all", label: "All" }];

  return (
    <>
      <div className="flex justify-between items-center mb-3 gap-3 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          {SF.map((f) => <button key={f.k} onClick={() => setSf(f.k)} data-testid={`ops-invoice-filter-${f.k}`} className={`pq-chip ${sf === f.k ? "pq-chip-active" : ""}`}>{f.label}</button>)}
        </div>
        <Btn onClick={() => setShowCreate(true)} data-testid="ops-new-invoice-btn">+ Raise on clinician's behalf</Btn>
      </div>
      <p className="text-[12.5px] text-slate-500 mb-3">Clinicians (vendors) submit payout invoices from their console. Verify &amp; approve them here, then mark as paid.</p>
      <Card>
        {!invoices ? <div className="flex justify-center py-16"><Spinner className="w-6 h-6 text-teal-600" /></div>
          : shown.length === 0 ? <EmptyState icon={<Wallet size={24} />} title="Nothing here" subtitle={sf === "submitted" ? "No invoices awaiting verification." : "No invoices in this state."} />
          : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead><tr>{["Invoice", "Clinician", "Period", "Source", "Amount", "Status", ""].map((h) => <th key={h} className="pq-label px-5 py-3 text-left border-b border-slate-100">{h}</th>)}</tr></thead>
            <tbody>
              {shown.map((i) => (
                <tr key={i.id} className="border-b border-slate-100 last:border-0" data-testid={`ops-invoice-row-${i.id}`}>
                  <td className="px-5 py-3 text-[13px] font-semibold">{i.invoice_no}</td>
                  <td className="px-5 py-3 text-[13px]">{i.clinician_name}</td>
                  <td className="px-5 py-3 text-[13px]">{i.period}</td>
                  <td className="px-5 py-3"><Badge tone={i.source === "clinician" ? "teal" : "slate"}>{i.source === "clinician" ? "Clinician" : "Ops"}</Badge></td>
                  <td className="px-5 py-3 metric text-[13px] font-bold">{money(i.amount, i.currency)}</td>
                  <td className="px-5 py-3"><Badge tone={i.status === "Paid" ? "emerald" : i.status === "Approved" ? "sky" : i.status === "Rejected" ? "rose" : "amber"}>{i.status}</Badge></td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    {i.status === "Submitted" && <>
                      <Btn size="sm" variant="primary" onClick={() => setStatus(i.id, "Approved")} data-testid={`ops-invoice-approve-${i.id}`}>Verify &amp; approve</Btn>
                      <Btn size="sm" className="pq-btn-danger ml-2" onClick={() => setRejecting(i)} data-testid={`ops-invoice-reject-${i.id}`}>Reject</Btn>
                    </>}
                    {i.status === "Approved" && <Btn size="sm" variant="primary" onClick={() => setStatus(i.id, "Paid")} data-testid={`ops-invoice-pay-${i.id}`}>Mark paid</Btn>}
                    {i.status === "Rejected" && i.reject_reason && <span className="text-[11.5px] text-rose-500">{i.reject_reason}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </Card>
      {showCreate && <CreateInvoice clinicians={clinicians} onClose={() => setShowCreate(false)} onDone={() => { setShowCreate(false); load(); onChange && onChange(); }} />}
      {rejecting && <RejectInvoice inv={rejecting} onClose={() => setRejecting(null)} onDone={(reason) => { setStatus(rejecting.id, "Rejected", { reject_reason: reason }); setRejecting(null); }} />}
    </>
  );
}

function RejectInvoice({ inv, onClose, onDone }) {
  const [reason, setReason] = useState("");
  return (
    <Modal title={`Reject ${inv.invoice_no}`} subtitle={`${inv.clinician_name} · ${inv.period}`} onClose={onClose}>
      <label className="block mb-4"><span className="block text-[13px] font-semibold text-slate-700 mb-1.5">Reason for rejection</span>
        <textarea className="pq-input min-h-[80px]" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Amount doesn't match completed sessions…" data-testid="ops-reject-reason" /></label>
      <div className="flex justify-end gap-2"><Btn onClick={onClose}>Cancel</Btn><Btn className="pq-btn-danger" onClick={() => onDone(reason)} data-testid="ops-reject-submit">Reject invoice</Btn></div>
    </Modal>
  );
}

function CreateInvoice({ clinicians, onClose, onDone }) {
  const [cid, setCid] = useState("");
  const [period, setPeriod] = useState(new Date().toLocaleString("en-US", { month: "short", year: "numeric" }));
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const create = async () => {
    if (!cid) return toast.error("Select a clinician");
    setSaving(true);
    try { await api.post("/ops/invoices", { clinician_id: cid, period, note }); toast.success("Vendor invoice generated"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSaving(false);
  };
  return (
    <Modal title="New Vendor Invoice" subtitle="Auto-totals the clinician's completed-case payouts" onClose={onClose}>
      <label className="block mb-3"><span className="block text-[13px] font-semibold text-slate-700 mb-1.5">Clinician (vendor)</span>
        <select className="pq-input" value={cid} onChange={(e) => setCid(e.target.value)} data-testid="ops-invoice-clinician">
          <option value="" disabled>Select…</option>
          {clinicians.map((c) => <option key={c.id} value={c.id}>{c.name} — {money(c.perf.payout_earned + c.perf.payout_pending)} lifetime</option>)}
        </select></label>
      <label className="block mb-3"><span className="block text-[13px] font-semibold text-slate-700 mb-1.5">Billing period</span>
        <input className="pq-input" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="ops-invoice-period" /></label>
      <label className="block mb-4"><span className="block text-[13px] font-semibold text-slate-700 mb-1.5">Note (optional)</span>
        <input className="pq-input" value={note} onChange={(e) => setNote(e.target.value)} data-testid="ops-invoice-note" /></label>
      <div className="flex justify-end gap-2"><Btn onClick={onClose}>Cancel</Btn><Btn variant="primary" onClick={create} disabled={saving} data-testid="ops-invoice-submit">{saving ? <Spinner className="w-4 h-4" /> : "Generate invoice"}</Btn></div>
    </Modal>
  );
}

function Agreements() {
  const [list, setList] = useState(null);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => api.get("/ops/clinicians").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!list) return <div className="flex justify-center py-20"><Spinner className="w-7 h-7 text-teal-600" /></div>;
  return (
    <>
      <p className="text-[12.5px] text-slate-500 mb-3">Vendor service agreements per clinician. Open a clinician to add or activate an agreement.</p>
      <Card>
        <div className="overflow-x-auto"><table className="w-full">
          <thead><tr>{["Clinician", "Specialty", "City", "Agreement", ""].map((h) => <th key={h} className="pq-label px-5 py-3 text-left border-b border-slate-100">{h}</th>)}</tr></thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`ops-agreement-row-${c.id}`}>
                <td className="px-5 py-3"><div className="flex items-center gap-2"><Avatar name={c.name} size={30} src={docFileUrl(c.photo_doc_id)} /><span className="text-[13px] font-semibold">{c.name}</span></div></td>
                <td className="px-5 py-3 text-[13px]">{c.specialty}</td>
                <td className="px-5 py-3 text-[13px]">{c.city}</td>
                <td className="px-5 py-3"><Badge tone={c.agreement_active ? "emerald" : "slate"}>{c.agreement_active ? "Active" : "None"}</Badge></td>
                <td className="px-5 py-3 text-right"><Btn size="sm" onClick={() => setOpenId(c.id)} data-testid={`ops-agreement-manage-${c.id}`}>Manage</Btn></td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Card>
      {openId && <ClinicianDetail cid={openId} initialTab="agreements" onClose={() => setOpenId(null)} onChange={load} />}
    </>
  );
}

function OpsRosterBoard() {
  const [shifts, setShifts] = useState(null);
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/ops/roster").then((r) => setShifts(r.data)), []);
  useEffect(() => { load(); api.get("/ops/patients").then((r) => setPatients(r.data)).catch(() => setPatients([])); }, [load]);
  const del = async (s) => {
    if (!window.confirm(`Remove ${s.clinician_name}'s ${s.date} ${s.start} shift?`)) return;
    await api.delete(`/ops/roster/${s.id}`); toast.success("Shift removed"); load();
  };
  if (!shifts) return <div className="flex justify-center py-20"><Spinner className="w-7 h-7 text-teal-600" /></div>;
  const shown = shifts.filter((s) => !q || `${s.clinician_name} ${s.patient_name} ${s.location}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <Card className="p-5" data-testid="ops-roster-board">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h3 className="font-display font-semibold text-[15px]">All-clinician roster</h3>
          <p className="text-[12.5px] text-slate-500">Every clinician's shifts. Add shifts from a clinician's detail → Roster tab.</p>
        </div>
        <SearchInput testid="ops-roster-board-search" value={q} onChange={setQ} placeholder="Filter by clinician, patient, location…" className="sm:w-72" />
      </div>
      <RosterViews shifts={shown} showClinician onEdit={setEdit} onDelete={del} prefix="ops-board" />
      {edit && <ShiftEditModal shift={edit} patients={patients} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </Card>
  );
}

function OpsVisitReports() {
  const [rows, setRows] = useState(null);
  const [view, setView] = useState(null);
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");
  const load = useCallback(() => api.get("/ops/visit-reports").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const push = async (r) => {
    try { await api.post(`/ops/visit-reports/${r.id}/push`); toast.success(`Report pushed to doctor — ${r.patient_name}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Push failed"); }
  };

  if (!rows) return <div className="flex justify-center py-20"><Spinner className="w-7 h-7 text-teal-600" /></div>;
  const filtered = rows.filter((r) => !q || `${r.patient_name} ${r.clinician_name} ${r.ref_no}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <Card className="p-5" data-testid="ops-visit-reports">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <CardHead title="Home visit reports" subtitle="Submitted by clinicians after each session. Review and push to the doctor." />
        <SearchInput testid="ops-reports-search" value={q} onChange={setQ} placeholder="Search patient or clinician…" className="sm:w-72" />
      </div>
      {filtered.length === 0 ? (
        <EmptyState icon={<FileText size={24} />} title="No visit reports yet" subtitle="Reports appear here when clinicians log sessions with their home visit report." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead><tr className="text-left text-slate-400 border-b border-slate-100">
              <th className="py-2 pr-3 font-semibold">Patient</th><th className="py-2 pr-3 font-semibold">Session</th>
              <th className="py-2 pr-3 font-semibold">Clinician</th><th className="py-2 pr-3 font-semibold">Visit</th>
              <th className="py-2 pr-3 font-semibold">Status</th><th className="py-2 font-semibold text-right">Actions</th>
            </tr></thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/60 cursor-pointer" onClick={() => setView(r)} data-testid={`ops-report-row-${r.id}`}>
                  <td className="py-2.5 pr-3"><div className="font-semibold text-slate-800">{r.patient_name}</div><div className="text-[11px] text-slate-400">{r.ref_no}</div></td>
                  <td className="py-2.5 pr-3">{reportSessionText(r)}</td>
                  <td className="py-2.5 pr-3">{r.clinician_name}</td>
                  <td className="py-2.5 pr-3">{r.visit_date}{r.visit_start ? ` · ${r.visit_start}` : ""}</td>
                  <td className="py-2.5 pr-3">{r.pushed_to_doctor ? <Badge tone="emerald">Pushed to doctor</Badge> : <Badge tone="amber">Submitted</Badge>}</td>
                  <td className="py-2.5" onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1.5 justify-end flex-wrap">
                      <Btn size="sm" onClick={() => setView(r)} data-testid={`ops-report-view-${r.id}`}><Eye size={13} /> View</Btn>
                      <Btn size="sm" onClick={() => setEdit(r)} data-testid={`ops-report-edit-${r.id}`}><Pencil size={13} /> Edit</Btn>
                      <Btn size="sm" onClick={() => window.open(reportPdfUrl(r.id), "_blank")} data-testid={`ops-report-pdf-${r.id}`}><Download size={13} /> PDF</Btn>
                      {!r.pushed_to_doctor && <Btn size="sm" variant="primary" onClick={() => push(r)} data-testid={`ops-report-push-${r.id}`}><Send size={13} /> Push to doctor</Btn>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {view && <VisitReportModal r={view} onClose={() => setView(null)} />}
      {edit && <VisitReportEditModal r={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </Card>
  );
}

function VisitReportEditModal({ r, onClose, onSaved }) {
  const [f, setF] = useState({
    visit_date: r.visit_date || "", visit_start: r.visit_start || "", visit_end: r.visit_end || "",
    location: r.location || "", height: r.height || "", weight: r.weight || "", ...(r.sections || {}),
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/ops/visit-reports/${r.id}`, {
        visit_date: f.visit_date, visit_start: f.visit_start, visit_end: f.visit_end,
        location: f.location, height: f.height, weight: f.weight,
        sections: Object.fromEntries(REPORT_SECTIONS.map(([k]) => [k, f[k] || ""])),
      });
      toast.success("Report updated"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update report"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Edit visit report — ${r.patient_name}`} subtitle={`${r.ref_no} · Session ${reportSessionText(r)} · by ${r.clinician_name}`} onClose={onClose} wide>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <label className="block"><span className="pq-label">Visit date</span>
          <input type="date" className="pq-input py-1.5 w-full" value={f.visit_date} onChange={(e) => set("visit_date", e.target.value)} data-testid="vre-date" /></label>
        <label className="block"><span className="pq-label">Start time</span>
          <input type="time" className="pq-input py-1.5 w-full" value={f.visit_start} onChange={(e) => set("visit_start", e.target.value)} data-testid="vre-start" /></label>
        <label className="block"><span className="pq-label">End time</span>
          <input type="time" className="pq-input py-1.5 w-full" value={f.visit_end} onChange={(e) => set("visit_end", e.target.value)} data-testid="vre-end" /></label>
        <label className="block"><span className="pq-label">Location</span>
          <input className="pq-input py-1.5 w-full" value={f.location} onChange={(e) => set("location", e.target.value)} data-testid="vre-location" /></label>
        <label className="block"><span className="pq-label">Height (cm)</span>
          <input className="pq-input py-1.5 w-full" value={f.height} onChange={(e) => set("height", e.target.value)} data-testid="vre-height" /></label>
        <label className="block"><span className="pq-label">Weight (kg)</span>
          <input className="pq-input py-1.5 w-full" value={f.weight} onChange={(e) => set("weight", e.target.value)} data-testid="vre-weight" /></label>
      </div>
      {REPORT_SECTIONS.map(([k, label], i) => (
        <label key={k} className="block mb-3"><span className="pq-label">{i + 1}. {label}</span>
          <textarea rows={3} className="pq-input py-1.5 w-full" value={f[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`vre-${k}`} /></label>
      ))}
      <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100">
        <Btn onClick={onClose} data-testid="vre-cancel">Cancel</Btn>
        <Btn variant="primary" onClick={save} disabled={busy} data-testid="vre-save">{busy ? "Saving…" : "Save changes"}</Btn>
      </div>
    </Modal>
  );
}
